'use strict';

var TouchPosition = require('../lib/TouchPosition.js');
var VelocityRecord = require('../lib/VelocityRecord.js');
var EventListenerOption = require('../lib/EventListenerOption.js');
var AFM = require('../lib/animate/FrameManager.js');
var Ease = require('../lib/animate/Ease.js');
var ElementTools = require('../lib/core/ElementTools.js');
var Listener = require('../lib/core/Listener.js');
var limitNumber = require('../lib/limitNumber.js');
var extend = require('extend');


// States
var STATES = {
	NONE : null,
	DRAGGING : 'DRAGGING',
	ANIMATING : 'ANIMATING',
	WHEELING : 'WHEELING'
};

// States
var SLIDE_SNAP_MODES = {
	UNRESTRICTED : 'UNRESTRICTED',
	RESTRICTED : 'RESTRICTED'	// Will proceed one snappoint on a quick swipe, in most situations.
};


// Options defaults
var defaults = {
	elements : {
		listenForEvents : null,
		move : null,
		viewport : null,
		contentSize : null
	},
	snap : {
		elements : null,
		leftEdges : true,
		rightEdges : true,
		centers : false,
		isAnimateToOnClick : true
	},
	sliding : {
		speedMultiplier : 500,
		duration : {
			min : 500,
			max : 3000,
			multiplier : 1.75
		},
		ease : Ease.outCubic
	},
	slideSnapMode : SLIDE_SNAP_MODES.RESTRICTED,
	wheeling : {
		timeout : 200
	},
	listeners : null
};
var EVENT_TYPE = {
	NONE : null,
	MOUSE : 'MOUSE',
	TOUCH : 'TOUCH'
};

// TODO Update this to be more like 'Accordion.js' where it has a setupAll() method, etc. Create a base class to do this stuff?
// TODO Switch to use 'sizzle' (https://sizzlejs.com/), jquery or none since there is more we can do and it is basically what people know already? And it returns an array.

/**
 * @typedef {object} SnapPoint
 * @property {number} x - The x that is snapped to.
 * @property {number} index - The index for the element in snap.elements we are snapped to.
 * @property {Element} element - The element
 * @property {String[]} positions - Indicates if this snapPoint is for the 'leftEdge', 'rightEdge', and/or
 * 				'center' position of the element. This will typically be one but is an array because it
 * 				could contain multiple values.
 */
/**
 * @typedef {object} InViewChangeData
 * @property {number} index - The index of this element in the snap elements provided.
 * @property {Element} element - The element this data is for.
 * @property {number} left - 0 means left edge of element is aligned with left edge of viewport,
 * 				and 1 means aligned with right edge of viewport. Values are not constrained and think of
 * 				them kind of like absolute CSS property 'left' when using absolute positioning, but
				numbers are not in percent form (e.i. 1 is 100%).
 * @property {number} right - Similar to 'left'. 0 means right edge of element is aligned with right
 * 				edge of viewport, and 1 means aligned with left edge of viewport.
 * @property {number} cover - A number indicating how much of the viewport this element covers.
 * 				This number will be between 0 and 1.
 */

/**
 * @class
 * @classdesc TODO. Construct and then call start().
 * 
 * This class supports listeners (on, off, etc.) via the Listener class.
 * 
 * The following events are triggered during the "read" phase of FrameManager via the
 * triggerDuringRead(...) method (via Listener class). Remember to
 * restrict DOM changes to the "write" phase of FrameManager.
 * If multiple events of the same type
 * happen within the same frame only the most recent will be reported.
 * Listeners this class triggers are:
 * <ul>
 * <li>activeChange - Triggered when the active element changes. Listener is passed
 * 			the previous (property 'previous') and new active element (property 'active').
 * 			Both are of type {@link SnapPoint}, or null.
 * <li>inViewChange - Triggered when the elements move. Listener is passed an array of
 * 			data (of type {@link InViewChangeData}) describing how each element sits
 * 			in relation to the viewport.
 * </ul>
 * TODO What other listeners should be added and should preventDefault do anything if called on them?
 * 
 * TODO update the rest of this which was a copy and paste from another project
 * @param {object} options - With the following configuration properties ...
 * @param {object} options.elements - TODO
 * @param {boolean} [options.TODO=true] - TODO
 */
function HorizontalScrollSnap(options) { // TODO The first argument needs to be the root element for this otherwise it is not compatible with the other components.
	// Options
	var opts = this._options = extend(true, {}, defaults, options);
	// TODO Check to make sure required options are set.
	
	// Adds a few basic listener methods (on, off and trigger) to this initiated object.
	Listener.init(this);
	
	// Setup any listeners that were specified in options
	this.onEach(opts.listeners);
	
	// Init a few properties
	this._velocityRecordX = new VelocityRecord(50, 6);
	this._currentTouchPosition = new TouchPosition();
	this._snapElementsTouchPosition = new TouchPosition();
	
	// Bind methods to 'this'
	this._onTouchStart = this._onTouchStart.bind(this);
	this._onTouchMove = this._onTouchMove.bind(this);
	this._onTouchEnd = this._onTouchEnd.bind(this);
	this._onWheel = this._onWheel.bind(this);
	this._onSnapElementsTouchStart = this._onSnapElementsTouchStart.bind(this);
	this._onSnapElementsTouchMove = this._onSnapElementsTouchMove.bind(this);
	this._onSnapElementsTouchEnd = this._onSnapElementsTouchEnd.bind(this);
	this._updateMeasurements = this._updateMeasurements.bind(this);
	this._onWindowResize = this._onWindowResize.bind(this);
	
	this.invalidateMeasurements = this.invalidateMeasurements.bind(this);
	this._draw = this._draw.bind(this);
}

HorizontalScrollSnap.COMPONENT_STYLE_CLASS = 'horizontalScrollSnap';

/**
 * @public
 */
HorizontalScrollSnap.prototype.start = function() {
	var opts = this._options;
	
	// Set starting values
	this._setState(STATES.NONE);
	this._wheelDeltaX = 0;
	this._activeEventType = EVENT_TYPE.NONE; // Keep track if the even is EVENT_TYPE.MOUSE or EVENT_TYPE.TOUCH or EVENT_TYPE.NONE
	
	// Setup event listeners on: opts.elements.listenForEvents
	var passiveListenerOpts = EventListenerOption.get(),
		nonpassiveListenerOpts = EventListenerOption.get(false, false);
	opts.elements.listenForEvents.addEventListener('mousedown', this._onTouchStart, passiveListenerOpts);
	opts.elements.listenForEvents.addEventListener('mousemove', this._onTouchMove, nonpassiveListenerOpts);
	opts.elements.listenForEvents.addEventListener('mouseup', this._onTouchEnd, passiveListenerOpts);
	opts.elements.listenForEvents.addEventListener('mouseleave', this._onTouchEnd, passiveListenerOpts);

	opts.elements.listenForEvents.addEventListener('touchstart', this._onTouchStart, passiveListenerOpts);
	opts.elements.listenForEvents.addEventListener('touchmove', this._onTouchMove, nonpassiveListenerOpts);
	opts.elements.listenForEvents.addEventListener('touchend', this._onTouchEnd, passiveListenerOpts);
	opts.elements.listenForEvents.addEventListener('touchcancel', this._onTouchEnd, passiveListenerOpts);
	
	opts.elements.listenForEvents.addEventListener('wheel', this._onWheel, passiveListenerOpts);
	
	opts.elements.listenForEvents.addEventListener('dragstart', _onNativeDragStart, nonpassiveListenerOpts);
	
	// Setup event listeners on: opts.snap.elements
	if (opts.snap.isAnimateToOnClick) {
		this._snapElementActiveEventType = EVENT_TYPE.NONE; // Keep track if the even is EVENT_TYPE.MOUSE or EVENT_TYPE.TOUCH or EVENT_TYPE.NONE
		for (var i=0; i < opts.snap.elements.length; i++) {
			opts.snap.elements[i].addEventListener('mousedown', this._onSnapElementsTouchStart, passiveListenerOpts);
//			opts.snap.elements[i].addEventListener('touchstart', this._onSnapElementsTouchStart, passiveListenerOpts);
			
			opts.snap.elements[i].addEventListener('mousemove', this._onSnapElementsTouchMove, passiveListenerOpts);
//			opts.snap.elements[i].addEventListener('touchmove', this._onSnapElementsTouchMove, passiveListenerOpts);
			
			opts.snap.elements[i].addEventListener('mouseup', this._onSnapElementsTouchEnd, passiveListenerOpts);
//			opts.snap.elements[i].addEventListener('touchend', this._onSnapElementsTouchEnd, passiveListenerOpts);
		}
	}
	
	// Listen for resize and take measurements
	AFM.on('read', this._updateMeasurements);
	window.addEventListener('resize', this._onWindowResize, EventListenerOption.get());
	
	// Draw during write
	AFM.on('write', this._draw);
	
	// Set to start position. Do next AFM's 'write' because we need _updateMeasurements to run first.
	var self = this;
	AFM.once('write', function() { self.setScrollX(0); });
}

/**
 * @public
 */
HorizontalScrollSnap.prototype.stop = function() {
	var opts = this._options;
	
	// Remove event listeners on: opts.elements.listenForEvents
	var passiveListenerOpts = EventListenerOption.get(),
		nonpassiveListenerOpts = EventListenerOption.get(false, true);
	opts.elements.listenForEvents.removeEventListener('mousedown', this._onTouchStart, passiveListenerOpts);
	opts.elements.listenForEvents.removeEventListener('mousemove', this._onTouchMove, nonpassiveListenerOpts);
	opts.elements.listenForEvents.removeEventListener('mouseup', this._onTouchEnd, passiveListenerOpts);
	opts.elements.listenForEvents.removeEventListener('mouseleave', this._onTouchEnd, passiveListenerOpts);
	
	opts.elements.listenForEvents.removeEventListener('touchstart', this._onTouchStart, passiveListenerOpts);
	opts.elements.listenForEvents.removeEventListener('touchmove', this._onTouchMove, nonpassiveListenerOpts);
	opts.elements.listenForEvents.removeEventListener('touchend', this._onTouchEnd, passiveListenerOpts);
	opts.elements.listenForEvents.removeEventListener('touchcancel', this._onTouchEnd, passiveListenerOpts);
	
	opts.elements.listenForEvents.removeEventListener('wheel', this._onWheel, passiveListenerOpts);
	
	opts.elements.listenForEvents.removeEventListener('dragstart', _onNativeDragStart, nonpassiveListenerOpts);
	
	// Remove event listeners on: opts.snap.elements
	if (opts.snap.isAnimateToOnClick) {
		for (var i=0; i < opts.snap.elements.length; i++) {
			opts.snap.elements[i].removeEventListener('mousedown', this._onSnapElementsTouchStart, passiveListenerOpts);
//			opts.snap.elements[i].removeEventListener('touchstart', this._onSnapElementsTouchStart, passiveListenerOpts);
			
			opts.snap.elements[i].removeEventListener('mousemove', this._onSnapElementsTouchMove, passiveListenerOpts);
//			opts.snap.elements[i].removeEventListener('touchmove', this._onSnapElementsTouchMove, passiveListenerOpts);
			
			opts.snap.elements[i].removeEventListener('mouseup', this._onSnapElementsTouchEnd, passiveListenerOpts);
//			opts.snap.elements[i].removeEventListener('touchend', this._onSnapElementsTouchEnd, passiveListenerOpts);
		}
	}
	
	// Remove listen for resize and take measurements
	AFM.off('read', this._updateMeasurements);
	window.removeEventListener('resize', this._onWindowResize, EventListenerOption.get());
	
	// remove draw during write
	AFM.off('write', this._draw);
}



/**
 * Constants used for animation states.
 * @public
 */
HorizontalScrollSnap.STATES = STATES;

/**
 * @private
 */
HorizontalScrollSnap.prototype._setState = function(state) {
	this._state = state;
}

/**
 * @public
 * @return {HorizontalScrollSnap.STATES} state - The current state.
 */
HorizontalScrollSnap.prototype.getState = function() {
	return this._state;
}

/**
 * @private
 */
HorizontalScrollSnap.prototype._onWindowResize = function() {
	this.invalidateMeasurements();
	this._hasWindowResized = true;
}

/**
 * @public
 * Invalidate next measurement and this class will automatically update
 * its measurements next 'read' portion of the frame.
 */
HorizontalScrollSnap.prototype.invalidateMeasurements = function() {
	this._needsRemeasuring = true;
}

/**
 * @private
 */
HorizontalScrollSnap.prototype._updateMeasurements = function() {
	if (this._needsRemeasuring !== false) {
		this._needsRemeasuring = false;
		var opts = this._options;
		
		// Calculate _maxScroll
		this._maxScroll =
			opts.elements.contentSize.scrollWidth
			- opts.elements.viewport.clientWidth;
		
		// Calculate _snapPoints {SnapPoint[]}	
		if (opts.snap.elements !== null) {
			this._snapPoints = new Array(); // [{x: , index: , element: }, ...]
			
			var moveRect = opts.elements.move.getBoundingClientRect(),
				vpRect = opts.elements.viewport.getBoundingClientRect(),
				element, elementRect,
				left, right, center,
				isLeftEdge = opts.snap.leftEdges === true,
				isRightEdge = opts.snap.rightEdges === true,
				isCenter = opts.snap.centers === true;
			
			// Build array of snap points
			for (var i=0; i < opts.snap.elements.length; i++) {
				element = opts.snap.elements[i];
				elementRect = element.getBoundingClientRect();
				
				if (isLeftEdge || isCenter) {
					left = elementRect.left - moveRect.left;
				}
				if (isRightEdge || isCenter) {
					right = elementRect.right - moveRect.left;
				}
				
				if (isLeftEdge) {
					this._snapPoints.push({
						index: i,
						element: element,
						x: parseInt(left),
						positions: ['leftEdge']
					});
				}
				if (isCenter) {
					this._snapPoints.push({
						index: i,
						element: element,
						x: parseInt((left + right - vpRect.width) / 2),
						positions: ['center']
					});
				}
				if (isRightEdge) {
					this._snapPoints.push({
						index: i,
						element: element,
						x: parseInt(right - vpRect.width),
						positions: ['rightEdge']
					});
				}
			}
			
			// Sort array, and remove duplicates
			if (this._snapPoints.length !== 0) {
				// Sort array
				this._snapPoints = this._snapPoints.sort(function(a, b) {
					  return a.x - b.x;
				});
				
				// Remove duplicates. Start loop at 1 as element 0 can never be a duplicate
				var retain = [this._snapPoints[0]],
					prev, curr;
				for (var i = 1; i < this._snapPoints.length; i++) {
					prev = this._snapPoints[i - 1];
					curr = this._snapPoints[i];
					if (prev.x === curr.x && prev.index === curr.index) {
						prev.positions.concat(curr.positions);
					} else {
						retain.push(curr);
					}
				}
				this._snapPoints = retain;
			}
		}
		
		// Update scroll position, if _updateMeasurement was the result of a window resize.
		if (this._hasWindowResized === true) {
			this._hasWindowResized = false;
			this._setState(STATES.NONE);
			var x = this._activeSnapPoint !== null && this._activeSnapPoint !== undefined ?
					this._activeSnapPoint.x : this.getScrollX();
			this.setScrollX(x);
		}
	}
}

/**
 * @private
 */
HorizontalScrollSnap.prototype._applyScrollLimits = function(x) {
	return limitNumber(x, 0, this._maxScroll);
}

/**
 * @private
 * @return {SnapPoint}
 */
HorizontalScrollSnap.prototype._findSnapPoint = function(x) {
	var closest = this._snapPoints.reduce(function (prev, curr) {
		return Math.abs(curr.x - x) < Math.abs(prev.x - x) ? curr : prev;
	});
	return closest;
}

/**
 * @private
 * @param {Element} element - The element to find the snapPoints for. Needs to be one of the 'snap.elements'
 * 		used to configure this instance in the first place.
 * @return {SnapPoint[]} - Returns an array of SnapPoints for the passed element, or undefined if not found.
HorizontalScrollSnap.prototype._findSnapPointsForElement = function(element) {
	var snapPoints = this._snapPoints.filter(function (curr) {
		return current.element === element;
	});
	return snapPoints;
}
 */

/**
 * @public
 * @param {(Element|number)} target - The element or index of the element to animate to. Element needs to be one of the
 * 		'snap.elements' used to configure this instance in the first place.
 * @return {boolean} - Returns true is successful. Unsuccessful may indicate index out of range, or element not found.
 */
HorizontalScrollSnap.prototype.animateTo = function(target) {
	// TODO Add a second argument to indicate preferred leftEdge, center or rightEdge. If not provided enhance this method to animate to the one that makes the most sense based on what is currently scrolled to.
	var targetSnapPoint = null,
		testProp;
	
	if (target instanceof Element) {
		testProp = 'element';
	} else {
		target = parseInt(target);
		if (isNaN(target)) {
			return false;
		}
		testProp = 'index';
	}
	
	for (var i=0, curr; i < this._snapPoints.length; i++) {
		curr = this._snapPoints[i];
		if (curr[testProp] === target) {
			targetSnapPoint = curr;
			break;
		}
	}
	
	if (targetSnapPoint !== null) {
		this._calculateAnimData(targetSnapPoint);
		this._setState(STATES.ANIMATING);
		return true;
	}
	
	return false;
}

/**
 * @private
 * Records what the current snapPoint is. It does NOT animate to it.
 * This is for recording purposes.
 */
HorizontalScrollSnap.prototype._setActiveSnapPoint = function(snapPoint) {
	if (snapPoint === undefined) {
		snapPoint = null;
	}
	if (snapPoint !== this._activeSnapPoint) {
		var prev = this._activeSnapPoint;
		this._activeSnapPoint = snapPoint;
		this.triggerDuringRead('activeChange', {
			previous : (prev === undefined) ? null : prev,
			active : (this._activeSnapPoint === undefined) ? null : this._activeSnapPoint
		});
	}
}

/**
 * @private
 */
HorizontalScrollSnap.prototype._clearActiveSnapPoint = function() {
	this._setActiveSnapPoint(null);
}

/**
 * @public
 */
HorizontalScrollSnap.prototype.getScrollX = function() {
	return this._scrollX;
}

/**
 * @public
 * @returns {SnapPoint}
 */
HorizontalScrollSnap.prototype.setScrollX = function(x) {
	var snapPoint = this._setScrollX(x, true, true);
	this._setActiveSnapPoint(snapPoint);
	return snapPoint;
}
/**
 * @public
 * @returns {SnapPoint}
 */
HorizontalScrollSnap.prototype._setScrollX = function(x, applyRange, applySnap) {
	var snapPoint = {x: x}; // Make a fake snapPoint for now.
	if (applyRange === true) {
		snapPoint.x = this._applyScrollLimits(x);
	}
	if (applySnap === true) {
		snapPoint = this._findSnapPoint(snapPoint.x);
	}
	
	if (snapPoint.x !== this._scrollX) {
		this._scrollX = snapPoint.x;
		var transformVal = 'translateX(' + (-1 * this._scrollX) + 'px)';
		ElementTools.setTransform(this._options.elements.move, transformVal);
		this._calculateElementsInViewPort();
	}
	
	return snapPoint;
}

/**
 * @private
 */
HorizontalScrollSnap.prototype._calculateElementsInViewPort = function() {
	if (this._options.snap.elements.length === 0) {
		return;
	}
	
	var opts = this._options,
		inViewData = new Array(),
		vpRect = opts.elements.viewport.getBoundingClientRect(),
		vpWidth = vpRect.width,
		element, elementRect,
		rightSubtract, leftSubtract, cover;
	
	for (var i=0; i < opts.snap.elements.length; i++) {
		element = opts.snap.elements[i];
		elementRect = element.getBoundingClientRect();
		
		if (elementRect.left >= vpRect.right || elementRect.right <= vpRect.left) {
			cover = 0;
		} else {
			leftSubtract = elementRect.left <= vpRect.left ? 0 : elementRect.left - vpRect.left;
			rightSubtract = elementRect.right >= vpRect.right ? 0 : vpRect.right - elementRect.right;
			cover = (vpWidth - (leftSubtract + rightSubtract)) / vpWidth;
		}
		
		inViewData.push({
			index : i,
			element : element,
			left : roundNumber((elementRect.left - vpRect.left) / vpWidth),
			right : roundNumber((vpRect.right - elementRect.right) / vpWidth),
			cover : roundNumber(cover)
		});
	}
	
	this.triggerDuringRead('inViewChange', inViewData);
}

/**
 * @private
 */
HorizontalScrollSnap.prototype._recordDrag = function() {
	var currentTouchX = this._currentTouchPosition.get().x;
	var deltaX = this._lastTouchPositionX - currentTouchX;
	this._lastTouchPositionX = currentTouchX;
	
	this._targetX = this._targetX + deltaX;
	
	var timestamp = performance.now();
	var timeDelta = timestamp - this._lastTouchTimestamp;
	this._lastTouchTimestamp = timestamp;
	
	this._velocityRecordX.record(deltaX, timeDelta);
}


/**
 * @private
 */
HorizontalScrollSnap.prototype._onTouchStart = function(event) {
	if (this._activeEventType === EVENT_TYPE.NONE) {
		this._activeEventType = event.type.indexOf('mouse') === 0 ?  EVENT_TYPE.MOUSE : EVENT_TYPE.TOUCH;
		
		this.invalidateMeasurements();
		
		this._velocityRecordX.clear();
		this._targetX = this.getScrollX();
		this._lastTouchPositionX = this._currentTouchPosition.setUsingEvent(event).x;
		this._lastTouchTimestamp = performance.now();
		
		this._onTouchStartActiveSnapPoint = this.getState() === STATES.ANIMATING ?
				this._animData.targetSnapPoint : this._activeSnapPoint;
		this._clearActiveSnapPoint();
		
		this._setState(STATES.DRAGGING);
	}
}

/**
 * @private
 */
function isActiveEventType(activeType, event) {
	return (activeType === EVENT_TYPE.MOUSE && event.type.indexOf('mouse') === 0)
		|| (activeType === EVENT_TYPE.TOUCH && event.type.indexOf('touch') === 0);
}

/**
 * @private
 */
HorizontalScrollSnap.prototype._onTouchMove = function(event) {
	if (isActiveEventType(this._activeEventType, event)) {
		var lastPosition = this._currentTouchPosition.get(),
			newPosition = this._currentTouchPosition.setUsingEvent(event);
		this._recordDrag();
		if (event.cancelable !== false 
			&& Math.abs(newPosition.x - lastPosition.x) > Math.abs(newPosition.y - lastPosition.y)) {
			// Stop scrolling and swiping that can cause some browsers to go back in history.
			// Only do this if the event is more horizontal than vertical
			event.preventDefault();
		}
	}
}

/**
 * @private
 */
HorizontalScrollSnap.prototype._calculateAnimData = function(targetSnapPoint) {
	var opt = this._options;
	
	this._targetX = targetSnapPoint.x;
	var startX = this.getScrollX();
	var distance = this._targetX - startX;
	
	this._animData = {
		startX : startX,
		distance : distance,
		startTime : performance.now(),
		duration : Math.round(
			Math.max(
				opt.sliding.duration.min,
				Math.min(
					opt.sliding.duration.max,
					Math.abs(distance) * opt.sliding.duration.multiplier
				)
			)
		),
		ease : opt.sliding.ease,
		targetSnapPoint : targetSnapPoint
	};
}

/**
 * @private
 */
HorizontalScrollSnap.prototype._onTouchEnd = function(event) {
	if (isActiveEventType(this._activeEventType, event)) {
		this._recordDrag();
		
		this._activeEventType = EVENT_TYPE.NONE;
		this._setState(STATES.ANIMATING);
		
		var speedX = this._velocityRecordX.getSpeed();
		
		var direction = this._velocityRecordX.getDirection();
		if (
			this._options.slideSnapMode === SLIDE_SNAP_MODES.RESTRICTED &&
			Math.abs(speedX) >= 1 &&
			direction !== null && this._onTouchStartActiveSnapPoint !== null
		) {
			// Meets criteria to move forward or back a single item

			// Find the active item's index
			var currentIndex = 0; // Default to a fallback value
			for (var i=0, snapPoint; i < this._snapPoints.length; i++) {
				snapPoint = this._snapPoints[i];
				if (
					snapPoint.index === this._onTouchStartActiveSnapPoint.index
					// && snapPoint.positions === this._onTouchStartActiveSnapPoint.positions
					&& snapPoint.x === this._onTouchStartActiveSnapPoint.x
				) {
					currentIndex = i;
					break;
				}
			}
			
			// Calculate new index and record as the target.
			var targetIndex = limitNumber(currentIndex + direction, 0, this._snapPoints.length - 1);
			targetSnapPoint = this._snapPoints[targetIndex];
		} else {
			// Meets criteria for momentum sliding
			// Adjust target based on speed of drag
			var targetX = this._applyScrollLimits(this._targetX + (speedX * this._options.sliding.speedMultiplier));
			// Find snapPoint, and calculate numbers for sliding
			var targetSnapPoint = this._findSnapPoint(targetX);
		}
		
		this._calculateAnimData(targetSnapPoint);
	}
}


/**
 * @private
 */
HorizontalScrollSnap.prototype._onWheel = function(event) {
 	if (event.deltaX !== undefined && event.deltaX !== 0) {
 		this._setState(STATES.WHEELING);
 		this._wheelDeltaX += event.deltaX;
 		this._wheelTimestamp = performance.now();
 	}
}

/**
 * @private
 */
var _onNativeDragStart = function(event){
	event.preventDefault();
}

/**
 * @private
 */
HorizontalScrollSnap.prototype._onSnapElementsTouchStart = function(event) {
	if (this._snapElementActiveEventType === EVENT_TYPE.NONE) {
		this._snapElementActiveEventType = event.type.indexOf('mouse') === 0 ?  EVENT_TYPE.MOUSE : EVENT_TYPE.TOUCH;
		this._snapElementsTouchPositionStartX = this._snapElementsTouchPosition.setUsingEvent(event).x;
	}
}

/**
 * @private
 */
HorizontalScrollSnap.prototype._onSnapElementsTouchMove = function(event) {
	if (isActiveEventType(this._snapElementActiveEventType, event)) {
		var x = this._snapElementsTouchPosition.setUsingEvent(event).x;
		if (Math.abs(x - this._snapElementsTouchPositionStartX) >= 5) {
			this._snapElementActiveEventType = EVENT_TYPE.NONE;
		}
	}
}

/**
 * @private
 */
HorizontalScrollSnap.prototype._onSnapElementsTouchEnd = function(event) {
	if (isActiveEventType(this._snapElementActiveEventType, event)) {
		this._snapElementActiveEventType = EVENT_TYPE.NONE;
		
		var targetElem = event.currentTarget;
		if (targetElem !== this._onTouchStartActiveSnapPoint.element) {
			this.animateTo(targetElem);
		}
	}
}


/**
 * @private
 */
HorizontalScrollSnap.prototype._draw = function(event, data) {
	switch(this.getState()) {
		// == NONE ==
		case STATES.NONE:
			break;
			
		// == DRAGGING ==
		case STATES.DRAGGING:
			var r = this._setScrollX(this._targetX, true, false);
			break;
		
		// == WHEELING ==
		case STATES.WHEELING:
			var targetX = this.getScrollX() + this._wheelDeltaX;
			this._wheelDeltaX = 0;
	 		
	 		if (performance.now() - this._wheelTimestamp < this._options.wheeling.timeout) {
	 			this._setScrollX(targetX, true, false);
	 		} else {
	 			var targetSnapPoint = this._findSnapPoint(targetX);
	 			this._calculateAnimData(targetSnapPoint);
	 			this._setState(STATES.ANIMATING);
	 		}
	 		
			break;
			
		// == ANIMATING ==
		case STATES.ANIMATING:
			var animData = this._animData;
			
			var p = animData.ease((data.timestamp - animData.startTime) / animData.duration);
			p = limitNumber(p, 0, 1);
			var moveX = animData.distance * p;
			
			if (Math.abs(animData.distance - moveX) > 0.05) {
				var newScrollX = animData.startX + moveX;
				this._setScrollX(newScrollX, false, false);
			} else {
				this._setScrollX(this._targetX, false, false);
				this._setActiveSnapPoint(animData.targetSnapPoint);
				this._setState(STATES.NONE);
			}
			break;
	}
}

/**
 * @private
 * Helper method for rounding numbers.
 */
function roundNumber(val) {
	return Math.round(val * 1000) / 1000;
}

module.exports = HorizontalScrollSnap;

